/**
 * Path-based locale helpers for marketing pages (/es, /en).
 *
 * Product routes (/dashboard, /login, …) stay unprefixed; UI language there
 * still comes from LanguageContext / localStorage.
 */

export const LOCALES = ['es', 'en'];
export const DEFAULT_LOCALE = 'es';

const APP_PREFIXES = [
  '/dashboard',
  '/login',
  '/activate',
  '/password',
];

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export const isAppPath = (pathname) => {
  const path = pathname || '/';
  return APP_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

/**
 * @param {string} pathname
 * @returns {'es'|'en'|null}
 */
export const getLocaleFromPath = (pathname) => {
  const segment = (pathname || '/').split('/').filter(Boolean)[0];
  if (segment === 'es' || segment === 'en') {
    return segment;
  }
  return null;
};

/**
 * Strip a leading /es or /en. Landing becomes "/".
 *
 * @param {string} pathname
 * @returns {string}
 */
export const stripLocale = (pathname) => {
  const path = pathname || '/';
  const parts = path.split('/').filter(Boolean);
  if (parts.length && LOCALES.includes(parts[0])) {
    const rest = parts.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Prefix a marketing path with a locale. App paths are returned unchanged.
 *
 * @param {string} path - Absolute path without locale (e.g. "/pricing", "/")
 * @param {'es'|'en'} lang
 * @returns {string}
 */
export const localizePath = (path, lang) => {
  const locale = lang === 'en' ? 'en' : 'es';
  if (!path || path === '/') {
    return `/${locale}`;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (isAppPath(normalized)) {
    return normalized;
  }
  const bare = stripLocale(normalized);
  if (bare === '/') {
    return `/${locale}`;
  }
  return `/${locale}${bare}`;
};

/**
 * Swap /es ↔ /en on a localized path. Non-localized paths are unchanged.
 *
 * @param {string} pathname
 * @param {'es'|'en'} lang
 * @returns {string}
 */
export const swapLocaleInPath = (pathname, lang) => {
  const locale = lang === 'en' ? 'en' : 'es';
  if (!getLocaleFromPath(pathname)) {
    return pathname || '/';
  }
  return localizePath(stripLocale(pathname), locale);
};

/**
 * Build absolute site URL for a localized marketing path.
 *
 * @param {string} siteUrl
 * @param {string} barePath - Path without locale ("/", "/pricing")
 * @param {'es'|'en'} lang
 * @returns {string}
 */
export const absoluteLocalizedUrl = (siteUrl, barePath, lang) => {
  const base = (siteUrl || '').replace(/\/$/, '');
  return `${base}${localizePath(barePath, lang)}`;
};
